import { MenuItem, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TravelerResponseDto } from '@bandido/shared/dtos';
import type { RuteroPassengerPayload } from '../queries';
import { IDENTITY_DOCUMENT_TYPES, identityDocuments, identityDocumentNeedsSelection } from '../transferIdentityDocument';
import type { IdentityDocumentType } from '../transferIdentityDocument';
import { PASSENGER_FIELD_MAX_LENGTH } from '../passengerFields';

export interface IdentityDocumentControlContext {
  passenger: RuteroPassengerPayload;
  traveler?: TravelerResponseDto | null;
  onChange: (partial: Partial<RuteroPassengerPayload>) => void;
}

export function TransferIdentityDocumentControl({
  passenger, traveler, onChange, required, error, disabled,
}: IdentityDocumentControlContext & {
  required: boolean; error?: boolean; disabled?: boolean;
}) {
  const { t } = useTranslation();
  const documents = identityDocuments(traveler);
  const selection = passenger.identityDocument;
  const needsSelection = identityDocumentNeedsSelection(passenger, traveler);
  const selectedId = selection?.id && documents.some((document) => document.id === selection.id)
    ? selection.id : undefined;
  return (
    <Stack spacing={1}>
      <TextField
        select size="small" fullWidth required={required} disabled={disabled}
        label={t('travelServices.booking.identityDocument')}
        value={needsSelection ? '' : selectedId ? `saved:${selectedId}` : selection ? `new:${selection.type}` : ''}
        error={needsSelection || (error && !selection)}
        helperText={needsSelection ? t('travelServices.booking.reselectIdentityDocument') : undefined}
        onChange={(event) => {
          const { value } = event.target;
          const document = documents.find((item) => `saved:${item.id}` === value);
          const type = document?.type.toLowerCase() ?? value.slice(4);
          if (!IDENTITY_DOCUMENT_TYPES.some((candidate) => candidate === type)) return;
          onChange({
            identityDocument: { type: type as IdentityDocumentType, ...(document ? { id: document.id } : {}) },
            passportNumber: document?.number ?? '',
            passportExpiry: undefined,
          });
        }}
      >
        <MenuItem value="" disabled>{t('travelServices.booking.chooseIdentityDocument')}</MenuItem>
        {documents.map((document) => (
          <MenuItem key={document.id} value={`saved:${document.id}`}>
            {t(`travelers.documents.types.${document.type.toLowerCase()}`)} · ••••{document.number.slice(-4)}
          </MenuItem>
        ))}
        {IDENTITY_DOCUMENT_TYPES.map((type) => (
          <MenuItem key={type} value={`new:${type}`}>
            {t('travelServices.booking.newIdentityDocument', { type: t(`travelers.documents.types.${type}`) })}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small" fullWidth required={required} disabled={disabled || !selection || needsSelection}
        label={t('travelers.documents.number')}
        value={passenger.passportNumber ?? ''} error={error}
        inputProps={{ maxLength: PASSENGER_FIELD_MAX_LENGTH.passportNumber }}
        onChange={(event) => onChange({ passportNumber: event.target.value })}
      />
    </Stack>
  );
}
